'use client';

import React, { useState } from 'react';
import Link from 'next/link';
import { FileText, Download, Building2, ShieldAlert, Clock, Filter, RefreshCw, BarChart3, FileBarChart, FileClock, FileCheck, FileWarning,  } from 'lucide-react';
import { VENDORS, ISSUES, DD_REVIEWS } from '@/lib/mockData';
import { toast } from 'sonner';

type ReportCategory = 'all' | 'vendor' | 'risk' | 'compliance' | 'contracts' | 'issues';

interface ReportDef {
  id: string;
  title: string;
  description: string;
  category: ReportCategory;
  icon: React.ElementType;
  iconColor: string;
  lastGenerated: string;
  format: 'PDF' | 'CSV' | 'Both';
  tags: string[];
}

const REPORTS: ReportDef[] = [
  {
    id: 'rpt-vendor-inventory',
    title: 'Vendor Inventory Report',
    description: 'Complete list of all vendors with risk tier, lifecycle stage, contract status, department, and open issues count.',
    category: 'vendor',
    icon: Building2,
    iconColor: 'text-teal-600',
    lastGenerated: '2026-03-28',
    format: 'Both',
    tags: ['Vendors', 'Inventory', 'All Tiers'],
  },
  {
    id: 'rpt-risk-summary',
    title: 'Risk Summary Report',
    description: 'Aggregated risk posture across all vendors including residual risk ratings, open critical findings, and risk trend by department.',
    category: 'risk',
    icon: ShieldAlert,
    iconColor: 'text-red-600',
    lastGenerated: '2026-03-27',
    format: 'PDF',
    tags: ['Risk', 'Critical', 'High', 'Findings'],
  },
  {
    id: 'rpt-compliance-status',
    title: 'Compliance Status Report',
    description: 'Due diligence completion rates, domain-level compliance scores, and outstanding review items by vendor and reviewer.',
    category: 'compliance',
    icon: FileCheck,
    iconColor: 'text-emerald-600',
    lastGenerated: '2026-03-25',
    format: 'Both',
    tags: ['DD', 'Compliance', 'Domains'],
  },
  {
    id: 'rpt-expiring-contracts',
    title: 'Expiring Contracts Report',
    description: 'All contracts expiring within 30, 60, and 90 days with contract value, owner, and renewal action status.',
    category: 'contracts',
    icon: FileClock,
    iconColor: 'text-amber-600',
    lastGenerated: '2026-03-29',
    format: 'Both',
    tags: ['Contracts', 'Expiring', 'Renewals'],
  },
  {
    id: 'rpt-open-issues',
    title: 'Open Issues Report',
    description: 'All open and in-remediation issues with severity, assigned owner, due date, and days overdue where applicable.',
    category: 'issues',
    icon: FileWarning,
    iconColor: 'text-orange-600',
    lastGenerated: '2026-03-28',
    format: 'Both',
    tags: ['Issues', 'Remediation', 'Overdue'],
  },
  {
    id: 'rpt-vendor-risk-heatmap',
    title: 'Vendor Risk Heatmap Report',
    description: 'Risk distribution by department and vendor tier, highlighting concentration risk and high-risk vendor clusters.',
    category: 'risk',
    icon: FileBarChart,
    iconColor: 'text-purple-600',
    lastGenerated: '2026-03-26',
    format: 'PDF',
    tags: ['Risk', 'Heatmap', 'Department'],
  },
  {
    id: 'rpt-dd-progress',
    title: 'Due Diligence Progress Report',
    description: 'Status of all active DD reviews including completion percentage, overdue items, and pending approvals.',
    category: 'compliance',
    icon: BarChart3,
    iconColor: 'text-blue-600',
    lastGenerated: '2026-03-24',
    format: 'Both',
    tags: ['DD', 'Progress', 'Overdue'],
  },
  {
    id: 'rpt-insurance-tracker',
    title: 'Insurance & COI Tracker',
    description: 'Certificate of Insurance status for all active vendors with expiration dates, coverage gaps, and renewal alerts.',
    category: 'compliance',
    icon: FileText,
    iconColor: 'text-indigo-600',
    lastGenerated: '2026-03-22',
    format: 'Both',
    tags: ['Insurance', 'COI', 'Expiring'],
  },
];

const CATEGORY_LABELS: Record<ReportCategory, string> = {
  all: 'All Reports',
  vendor: 'Vendor Inventory',
  risk: 'Risk',
  compliance: 'Compliance',
  contracts: 'Contracts',
  issues: 'Issues',
};

function generateVendorInventoryPDF() {
  const rows = VENDORS.map((v) => `
    <tr>
      <td>${v.vendorId}</td>
      <td>${v.name}</td>
      <td>${v.department}</td>
      <td>${v.category}</td>
      <td><span style="color:${v.riskTier === 'Critical' ? '#dc2626' : v.riskTier === 'High' ? '#ea580c' : v.riskTier === 'Moderate' ? '#d97706' : '#16a34a'}">${v.riskTier}</span></td>
      <td>${v.lifecycleStage}</td>
      <td>${v.contractStatus}</td>
      <td>${v.openIssues}</td>
    </tr>`).join('');

  return `<!DOCTYPE html><html><head><title>Vendor Inventory Report</title>
  <style>body{font-family:Arial,sans-serif;padding:32px;color:#111}h1{color:#0f766e;font-size:22px;margin-bottom:4px}
  .meta{color:#6b7280;font-size:12px;margin-bottom:24px}table{width:100%;border-collapse:collapse;font-size:12px}
  th{background:#f0fdf4;color:#065f46;padding:8px 10px;text-align:left;border-bottom:2px solid #d1fae5}
  td{padding:7px 10px;border-bottom:1px solid #e5e7eb}tr:hover td{background:#f9fafb}
  .kpi{display:inline-block;background:#f0fdf4;border:1px solid #a7f3d0;border-radius:8px;padding:12px 20px;margin:0 8px 16px 0;text-align:center}
  .kpi-val{font-size:24px;font-weight:700;color:#0f766e}.kpi-lbl{font-size:11px;color:#6b7280;margin-top:2px}
  </style></head><body>
  <h1>Vendor Inventory Report</h1>
  <p class="meta">City of Pleasanton · Generated ${new Date().toLocaleDateString()} · CityTPRM</p>
  <div>
    <div class="kpi"><div class="kpi-val">${VENDORS.length}</div><div class="kpi-lbl">Total Vendors</div></div>
    <div class="kpi"><div class="kpi-val">${VENDORS.filter((v) => v.riskTier === 'Critical').length}</div><div class="kpi-lbl">Critical Risk</div></div>
    <div class="kpi"><div class="kpi-val">${VENDORS.filter((v) => v.lifecycleStage === 'Active').length}</div><div class="kpi-lbl">Active</div></div>
    <div class="kpi"><div class="kpi-val">${VENDORS.reduce((a, v) => a + v.openIssues, 0)}</div><div class="kpi-lbl">Open Issues</div></div>
  </div>
  <table><thead><tr><th>Vendor ID</th><th>Name</th><th>Department</th><th>Category</th><th>Risk Tier</th><th>Stage</th><th>Contract Status</th><th>Open Issues</th></tr></thead>
  <tbody>${rows}</tbody></table></body></html>`;
}

function generateRiskSummaryPDF() {
  const byTier = ['Critical', 'High', 'Moderate', 'Low'].map((t) => ({
    tier: t,
    count: VENDORS.filter((v) => v.riskTier === t).length,
  }));
  const criticalIssues = ISSUES.filter((i) => i.severity === 'Critical' && i.status !== 'Closed');
  const issueRows = criticalIssues.map((i) => `
    <tr><td>${i.id}</td><td>${i.vendorName}</td><td>${i.category}</td><td style="color:#dc2626">${i.severity}</td><td>${i.status}</td><td>${i.dueDate}</td></tr>`).join('');

  return `<!DOCTYPE html><html><head><title>Risk Summary Report</title>
  <style>body{font-family:Arial,sans-serif;padding:32px;color:#111}h1{color:#dc2626;font-size:22px;margin-bottom:4px}
  .meta{color:#6b7280;font-size:12px;margin-bottom:24px}h2{font-size:14px;color:#374151;margin:24px 0 8px}
  table{width:100%;border-collapse:collapse;font-size:12px}
  th{background:#fef2f2;color:#991b1b;padding:8px 10px;text-align:left;border-bottom:2px solid #fecaca}
  td{padding:7px 10px;border-bottom:1px solid #e5e7eb}
  .kpi{display:inline-block;background:#fef2f2;border:1px solid #fecaca;border-radius:8px;padding:12px 20px;margin:0 8px 16px 0;text-align:center}
  .kpi-val{font-size:24px;font-weight:700;color:#dc2626}.kpi-lbl{font-size:11px;color:#6b7280;margin-top:2px}
  .tier-bar{display:flex;align-items:center;gap:12px;margin:6px 0;font-size:12px}
  .bar{height:16px;border-radius:4px;background:#dc2626;min-width:4px}
  </style></head><body>
  <h1>Risk Summary Report</h1>
  <p class="meta">City of Pleasanton · Generated ${new Date().toLocaleDateString()} · CityTPRM</p>
  <div>
    ${byTier.map((t) => `<div class="kpi"><div class="kpi-val">${t.count}</div><div class="kpi-lbl">${t.tier} Risk</div></div>`).join('')}
  </div>
  <h2>Risk Distribution by Tier</h2>
  ${byTier.map((t) => `<div class="tier-bar"><span style="width:80px">${t.tier}</span><div class="bar" style="width:${Math.max(t.count * 20, 4)}px;background:${t.tier === 'Critical' ? '#dc2626' : t.tier === 'High' ? '#ea580c' : t.tier === 'Moderate' ? '#d97706' : '#16a34a'}"></div><span>${t.count} vendors</span></div>`).join('')}
  <h2>Critical Open Issues (${criticalIssues.length})</h2>
  <table><thead><tr><th>Issue ID</th><th>Vendor</th><th>Category</th><th>Severity</th><th>Status</th><th>Due Date</th></tr></thead>
  <tbody>${issueRows || '<tr><td colspan="6" style="text-align:center;color:#6b7280">No critical open issues</td></tr>'}</tbody></table>
  </body></html>`;
}

function generateComplianceStatusPDF() {
  const ddRows = DD_REVIEWS.map((r) => `
    <tr><td>${r.id}</td><td>${r.vendorName}</td><td>${r.domain}</td><td>${r.status}</td><td>${r.reviewer}</td>
    <td><div style="background:#e5e7eb;border-radius:4px;height:10px;width:80px;display:inline-block;vertical-align:middle">
    <div style="background:${r.completionPct === 100 ? '#16a34a' : r.status === 'Overdue' ? '#dc2626' : '#0d9488'};height:10px;border-radius:4px;width:${r.completionPct}%"></div></div> ${r.completionPct}%</td>
    <td>${r.dueDate}</td></tr>`).join('');

  return `<!DOCTYPE html><html><head><title>Compliance Status Report</title>
  <style>body{font-family:Arial,sans-serif;padding:32px;color:#111}h1{color:#059669;font-size:22px;margin-bottom:4px}
  .meta{color:#6b7280;font-size:12px;margin-bottom:24px}
  table{width:100%;border-collapse:collapse;font-size:12px}
  th{background:#f0fdf4;color:#065f46;padding:8px 10px;text-align:left;border-bottom:2px solid #a7f3d0}
  td{padding:7px 10px;border-bottom:1px solid #e5e7eb}
  .kpi{display:inline-block;background:#f0fdf4;border:1px solid #a7f3d0;border-radius:8px;padding:12px 20px;margin:0 8px 16px 0;text-align:center}
  .kpi-val{font-size:24px;font-weight:700;color:#059669}.kpi-lbl{font-size:11px;color:#6b7280;margin-top:2px}
  </style></head><body>
  <h1>Compliance Status Report</h1>
  <p class="meta">City of Pleasanton · Generated ${new Date().toLocaleDateString()} · CityTPRM</p>
  <div>
    <div class="kpi"><div class="kpi-val">${DD_REVIEWS.length}</div><div class="kpi-lbl">Total Reviews</div></div>
    <div class="kpi"><div class="kpi-val">${DD_REVIEWS.filter((r) => r.status === 'Complete').length}</div><div class="kpi-lbl">Complete</div></div>
    <div class="kpi"><div class="kpi-val">${DD_REVIEWS.filter((r) => r.status === 'Overdue').length}</div><div class="kpi-lbl">Overdue</div></div>
    <div class="kpi"><div class="kpi-val">${DD_REVIEWS.filter((r) => r.status === 'Pending Approval').length}</div><div class="kpi-lbl">Pending Approval</div></div>
  </div>
  <table><thead><tr><th>Review ID</th><th>Vendor</th><th>Domain</th><th>Status</th><th>Reviewer</th><th>Completion</th><th>Due Date</th></tr></thead>
  <tbody>${ddRows}</tbody></table></body></html>`;
}

function generateExpiringContractsPDF() {
  const today = new Date('2026-03-29');
  const expiring = VENDORS
    .filter((v) => {
      if (!v.contractEnd) return false;
      const end = new Date(v.contractEnd);
      const diff = (end.getTime() - today.getTime()) / (1000 * 60 * 60 * 24);
      return diff >= 0 && diff <= 90;
    })
    .map((v) => {
      const end = new Date(v.contractEnd);
      const days = Math.round((end.getTime() - today.getTime()) / (1000 * 60 * 60 * 24));
      return { ...v, daysLeft: days };
    })
    .sort((a, b) => a.daysLeft - b.daysLeft);

  const rows = expiring.map((v) => `
    <tr><td>${v.vendorId}</td><td>${v.name}</td><td>${v.contractOwner}</td>
    <td style="color:${v.daysLeft <= 30 ? '#dc2626' : v.daysLeft <= 60 ? '#d97706' : '#374151'}">${v.daysLeft} days</td>
    <td>${v.contractEnd}</td><td>${v.contractStatus}</td></tr>`).join('');

  return `<!DOCTYPE html><html><head><title>Expiring Contracts Report</title>
  <style>body{font-family:Arial,sans-serif;padding:32px;color:#111}h1{color:#d97706;font-size:22px;margin-bottom:4px}
  .meta{color:#6b7280;font-size:12px;margin-bottom:24px}
  table{width:100%;border-collapse:collapse;font-size:12px}
  th{background:#fffbeb;color:#92400e;padding:8px 10px;text-align:left;border-bottom:2px solid #fde68a}
  td{padding:7px 10px;border-bottom:1px solid #e5e7eb}
  .kpi{display:inline-block;background:#fffbeb;border:1px solid #fde68a;border-radius:8px;padding:12px 20px;margin:0 8px 16px 0;text-align:center}
  .kpi-val{font-size:24px;font-weight:700;color:#d97706}.kpi-lbl{font-size:11px;color:#6b7280;margin-top:2px}
  </style></head><body>
  <h1>Expiring Contracts Report</h1>
  <p class="meta">City of Pleasanton · Generated ${new Date().toLocaleDateString()} · As of 2026-03-29 · CityTPRM</p>
  <div>
    <div class="kpi"><div class="kpi-val">${expiring.filter((v) => v.daysLeft <= 30).length}</div><div class="kpi-lbl">Expiring ≤30 days</div></div>
    <div class="kpi"><div class="kpi-val">${expiring.filter((v) => v.daysLeft > 30 && v.daysLeft <= 60).length}</div><div class="kpi-lbl">Expiring 31–60 days</div></div>
    <div class="kpi"><div class="kpi-val">${expiring.filter((v) => v.daysLeft > 60).length}</div><div class="kpi-lbl">Expiring 61–90 days</div></div>
  </div>
  <table><thead><tr><th>Vendor ID</th><th>Vendor</th><th>Owner</th><th>Days Left</th><th>End Date</th><th>Status</th></tr></thead>
  <tbody>${rows || '<tr><td colspan="6" style="text-align:center;color:#6b7280">No contracts expiring within 90 days</td></tr>'}</tbody></table>
  </body></html>`;
}

function generateOpenIssuesPDF() {
  const open = ISSUES.filter((i) => i.status !== 'Closed');
  const today = new Date('2026-03-29');
  const rows = open.map((i) => {
    const due = new Date(i.dueDate);
    const overdue = (today.getTime() - due.getTime()) / (1000 * 60 * 60 * 24);
    return `<tr>
      <td>${i.id}</td><td>${i.vendorName}</td><td>${i.category}</td>
      <td style="color:${i.severity === 'Critical' ? '#dc2626' : i.severity === 'High' ? '#ea580c' : '#d97706'}">${i.severity}</td>
      <td>${i.status}</td><td>${i.assignedOwner}</td><td>${i.dueDate}</td>
      <td style="color:${overdue > 0 ? '#dc2626' : '#16a34a'}">${overdue > 0 ? `${Math.round(overdue)}d overdue` : 'On track'}</td>
    </tr>`;
  }).join('');

  return `<!DOCTYPE html><html><head><title>Open Issues Report</title>
  <style>body{font-family:Arial,sans-serif;padding:32px;color:#111}h1{color:#ea580c;font-size:22px;margin-bottom:4px}
  .meta{color:#6b7280;font-size:12px;margin-bottom:24px}
  table{width:100%;border-collapse:collapse;font-size:12px}
  th{background:#fff7ed;color:#9a3412;padding:8px 10px;text-align:left;border-bottom:2px solid #fed7aa}
  td{padding:7px 10px;border-bottom:1px solid #e5e7eb}
  .kpi{display:inline-block;background:#fff7ed;border:1px solid #fed7aa;border-radius:8px;padding:12px 20px;margin:0 8px 16px 0;text-align:center}
  .kpi-val{font-size:24px;font-weight:700;color:#ea580c}.kpi-lbl{font-size:11px;color:#6b7280;margin-top:2px}
  </style></head><body>
  <h1>Open Issues Report</h1>
  <p class="meta">City of Pleasanton · Generated ${new Date().toLocaleDateString()} · CityTPRM</p>
  <div>
    <div class="kpi"><div class="kpi-val">${open.length}</div><div class="kpi-lbl">Total Open</div></div>
    <div class="kpi"><div class="kpi-val">${open.filter((i) => i.severity === 'Critical').length}</div><div class="kpi-lbl">Critical</div></div>
    <div class="kpi"><div class="kpi-val">${open.filter((i) => i.severity === 'High').length}</div><div class="kpi-lbl">High</div></div>
    <div class="kpi"><div class="kpi-val">${open.filter((i) => i.status === 'Remediation').length}</div><div class="kpi-lbl">In Remediation</div></div>
  </div>
  <table><thead><tr><th>Issue ID</th><th>Vendor</th><th>Category</th><th>Severity</th><th>Status</th><th>Owner</th><th>Due Date</th><th>Timeline</th></tr></thead>
  <tbody>${rows}</tbody></table></body></html>`;
}

function generateCSV(reportId: string): string {
  if (reportId === 'rpt-vendor-inventory') {
    const header = 'Vendor ID,Name,Department,Category,Risk Tier,Lifecycle Stage,Contract Status,Open Issues\n';
    const rows = VENDORS.map((v) => `${v.vendorId},"${v.name}","${v.department}","${v.category}",${v.riskTier},${v.lifecycleStage},${v.contractStatus},${v.openIssues}`).join('\n');
    return header + rows;
  }
  if (reportId === 'rpt-open-issues') {
    const open = ISSUES.filter((i) => i.status !== 'Closed');
    const header = 'Issue ID,Vendor,Category,Severity,Status,Owner,Due Date\n';
    const rows = open.map((i) => `${i.id},"${i.vendorName}","${i.category}",${i.severity},${i.status},"${i.assignedOwner}",${i.dueDate}`).join('\n');
    return header + rows;
  }
  if (reportId === 'rpt-compliance-status' || reportId === 'rpt-dd-progress') {
    const header = 'Review ID,Vendor,Domain,Status,Reviewer,Completion %,Due Date\n';
    const rows = DD_REVIEWS.map((r) => `${r.id},"${r.vendorName}",${r.domain},${r.status},"${r.reviewer}",${r.completionPct}%,${r.dueDate}`).join('\n');
    return header + rows;
  }
  return 'No CSV data available for this report.';
}

function downloadPDF(html: string, filename: string) {
  const win = window.open('', '_blank');
  if (!win) { toast.error('Popup blocked. Please allow popups for this site.'); return; }
  win.document.write(html);
  win.document.close();
  setTimeout(() => { win.print(); }, 500);
  toast.success('Report opened', { description: `${filename} opened in a new tab. Use Print → Save as PDF.` });
}

function downloadCSV(content: string, filename: string) {
  const blob = new Blob([content], { type: 'text/csv' });
  const url = URL.createObjectURL(blob);
  const a = document.createElement('a');
  a.href = url;
  a.download = filename;
  a.click();
  URL.revokeObjectURL(url);
  toast.success('CSV downloaded', { description: `${filename} saved to your downloads.` });
}

function handleDownload(report: ReportDef, format: 'PDF' | 'CSV') {
  const dateStr = new Date().toISOString().slice(0, 10);
  if (format === 'CSV') {
    const csv = generateCSV(report.id);
    downloadCSV(csv, `${report.id}-${dateStr}.csv`);
    return;
  }
  let html = '';
  if (report.id === 'rpt-vendor-inventory') html = generateVendorInventoryPDF();
  else if (report.id === 'rpt-risk-summary' || report.id === 'rpt-vendor-risk-heatmap') html = generateRiskSummaryPDF();
  else if (report.id === 'rpt-compliance-status' || report.id === 'rpt-dd-progress' || report.id === 'rpt-insurance-tracker') html = generateComplianceStatusPDF();
  else if (report.id === 'rpt-expiring-contracts') html = generateExpiringContractsPDF();
  else if (report.id === 'rpt-open-issues') html = generateOpenIssuesPDF();
  else html = generateVendorInventoryPDF();
  downloadPDF(html, `${report.id}-${dateStr}.pdf`);
}

export default function ReportsModule() {
  const [activeCategory, setActiveCategory] = useState<ReportCategory>('all');
  const [searchQuery, setSearchQuery] = useState('');

  const filtered = REPORTS.filter((r) => {
    const matchCat = activeCategory === 'all' || r.category === activeCategory;
    const q = searchQuery.toLowerCase();
    const matchQ = !q || r.title.toLowerCase().includes(q) || r.description.toLowerCase().includes(q) || r.tags.some((t) => t.toLowerCase().includes(q));
    return matchCat && matchQ;
  });

  const categoryCounts: Record<ReportCategory, number> = {
    all: REPORTS.length,
    vendor: REPORTS.filter((r) => r.category === 'vendor').length,
    risk: REPORTS.filter((r) => r.category === 'risk').length,
    compliance: REPORTS.filter((r) => r.category === 'compliance').length,
    contracts: REPORTS.filter((r) => r.category === 'contracts').length,
    issues: REPORTS.filter((r) => r.category === 'issues').length,
  };

  return (
    <div className="flex flex-col h-full min-h-0">
      {/* Header */}
      <div className="px-5 py-3 border-b border-[hsl(var(--border))] bg-[hsl(var(--surface))] flex items-center justify-between gap-3 flex-shrink-0">
        <div>
          <h1 className="text-xl font-bold text-[hsl(var(--text-primary))]">Reports</h1>
          <p className="text-xs text-[hsl(var(--text-muted))] mt-0.5">
            {REPORTS.length} reports available · Download as PDF or CSV
          </p>
        </div>
        <div className="flex items-center gap-2">
          <Link
            href="/executive-dashboard"
            className="btn-secondary"
          >
            ← Home
          </Link>
          <button
            onClick={() => toast.info('Scheduled reports coming soon.')}
            className="btn-secondary"
          >
            <Clock size={13} />
            Schedule Report
          </button>
        </div>
      </div>

      {/* Summary strip */}
      <div className="flex border-b border-[hsl(var(--border))] bg-[hsl(var(--background))] flex-shrink-0 divide-x divide-[hsl(var(--border))]">
        {[
          { label: 'Total Vendors', value: VENDORS.length, color: 'text-teal-600' },
          { label: 'Critical Risk', value: VENDORS.filter((v) => v.riskTier === 'Critical').length, color: 'text-red-600' },
          { label: 'Open Issues', value: ISSUES.filter((i) => i.status !== 'Closed').length, color: 'text-orange-600' },
          { label: 'DD Reviews', value: DD_REVIEWS.length, color: 'text-blue-600' },
          { label: 'Overdue Reviews', value: DD_REVIEWS.filter((r) => r.status === 'Overdue').length, color: 'text-amber-600' },
        ].map((s) => (
          <div key={s.label} className="flex-1 px-4 py-2.5 text-center">
            <p className={`text-lg font-bold tabular-nums ${s.color}`}>{s.value}</p>
            <p className="text-2xs text-[hsl(var(--text-muted))] mt-0.5">{s.label}</p>
          </div>
        ))}
      </div>

      {/* Filters */}
      <div className="px-5 py-3 border-b border-[hsl(var(--border))] bg-[hsl(var(--surface))] flex items-center gap-3 flex-shrink-0 flex-wrap">
        <div className="relative flex-1 min-w-48 max-w-xs">
          <Filter size={12} className="absolute left-2.5 top-1/2 -translate-y-1/2 text-[hsl(var(--text-muted))]" />
          <input
            type="text"
            value={searchQuery}
            onChange={(e) => setSearchQuery(e.target.value)}
            placeholder="Search reports…"
            className="input-base pl-8 text-xs h-8 w-full"
          />
        </div>
        <div className="flex items-center gap-1.5 flex-wrap">
          {(Object.keys(CATEGORY_LABELS) as ReportCategory[]).map((cat) => (
            <button
              key={cat}
              onClick={() => setActiveCategory(cat)}
              className={`px-3 py-1.5 text-xs font-medium rounded-md border transition-all ${
                activeCategory === cat
                  ? 'bg-teal-600 border-teal-600 text-white' :'bg-[hsl(var(--surface))] border-[hsl(var(--border))] text-[hsl(var(--text-secondary))] hover:border-[hsl(var(--border-strong))]'
              }`}
            >
              {CATEGORY_LABELS[cat]}
              <span className={`ml-1.5 text-2xs tabular-nums ${activeCategory === cat ? 'text-teal-100' : 'text-[hsl(var(--text-muted))]'}`}>
                {categoryCounts[cat]}
              </span>
            </button>
          ))}
        </div>
      </div>

      {/* Report grid */}
      <div className="flex-1 overflow-y-auto p-5">
        {filtered.length === 0 ? (
          <div className="py-16 text-center">
            <FileText size={32} className="mx-auto text-[hsl(var(--text-muted))] opacity-30 mb-3" />
            <p className="text-sm font-medium text-[hsl(var(--text-secondary))]">No reports match your filters</p>
            <p className="text-xs text-[hsl(var(--text-muted))] mt-1">Try adjusting your search or category filter.</p>
          </div>
        ) : (
          <div className="grid grid-cols-1 md:grid-cols-2 xl:grid-cols-3 gap-4">
            {filtered.map((report) => {
              const ReportIcon = report.icon;
              return (
                <div
                  key={report.id}
                  className="flex flex-col p-4 rounded-lg border border-[hsl(var(--border))] bg-[hsl(var(--surface))] hover:border-[hsl(var(--border-strong))] hover:shadow-sm transition-all"
                >
                  {/* Card header */}
                  <div className="flex items-start gap-3 mb-3">
                    <div className={`p-2 rounded-md bg-[hsl(var(--background))] ${report.iconColor} flex-shrink-0`}>
                      <ReportIcon size={18} />
                    </div>
                    <div className="flex-1 min-w-0">
                      <h3 className="text-sm font-semibold text-[hsl(var(--text-primary))] leading-snug">{report.title}</h3>
                      <div className="flex items-center gap-1.5 mt-1 flex-wrap">
                        {report.tags.map((tag) => (
                          <span key={tag} className="text-2xs px-1.5 py-0.5 rounded bg-[hsl(var(--background))] border border-[hsl(var(--border))] text-[hsl(var(--text-muted))]">
                            {tag}
                          </span>
                        ))}
                      </div>
                    </div>
                  </div>

                  {/* Description */}
                  <p className="text-xs text-[hsl(var(--text-secondary))] leading-relaxed flex-1 mb-4">
                    {report.description}
                  </p>

                  {/* Footer */}
                  <div className="flex items-center justify-between pt-3 border-t border-[hsl(var(--border))]">
                    <div className="flex items-center gap-1 text-2xs text-[hsl(var(--text-muted))]">
                      <RefreshCw size={10} />
                      <span>Last: {report.lastGenerated}</span>
                    </div>
                    <div className="flex items-center gap-1.5">
                      {(report.format === 'PDF' || report.format === 'Both') && (
                        <button
                          onClick={() => handleDownload(report, 'PDF')}
                          className="flex items-center gap-1 px-2.5 py-1.5 text-xs font-medium rounded-md bg-teal-600 hover:bg-teal-700 text-white transition-colors"
                        >
                          <Download size={11} />
                          PDF
                        </button>
                      )}
                      {(report.format === 'CSV' || report.format === 'Both') && (
                        <button
                          onClick={() => handleDownload(report, 'CSV')}
                          className="flex items-center gap-1 px-2.5 py-1.5 text-xs font-medium rounded-md border border-[hsl(var(--border))] bg-[hsl(var(--surface))] hover:bg-[hsl(var(--background))] text-[hsl(var(--text-secondary))] transition-colors"
                        >
                          <Download size={11} />
                          CSV
                        </button>
                      )}
                    </div>
                  </div>
                </div>
              );
            })}
          </div>
        )}
      </div>
    </div>
  );
}
